import Big from 'big.js';
import { useState, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { RecoilState, Snapshot, atom, selector, useRecoilValue, useRecoilCallback } from 'recoil';

export type CellSetter = (recoilVal: RecoilState<Big>, valOrUpdater: ((currVal: Big) => Big) | Big) => void;
type CleanUp = () => void;
type UndoFn = (snapshot: Snapshot, set: CellSetter) => Promise<void>;
type RedoFn = (snapshot: Snapshot, set: CellSetter) => Promise<void>;
type UndoPoint = [UndoFn, RedoFn] | [UndoFn, RedoFn, CleanUp];

export const runVoidCallbackAsync = async (cb: () => Promise<void> | void) => {
    try {
        await cb();
    } catch (error) {
        return error;
    }
};

const pastUndoStackAtom = atom<UndoPoint[]>({
    key: 'undostack',
    default: [],
});

const futureRedoStackAtom = atom<UndoPoint[]>({
    key: 'redostack',
    default: [],
});

const isInPastAtom = atom({
    key: 'isInPast',
    default: false,
});

const undoStackCountSelector = selector({
    key: 'undostackCount',
    get: ({ get }) => get(pastUndoStackAtom).length,
});

const redoStackCountSelector = selector({
    key: 'redostackCount',
    get: ({ get }) => get(futureRedoStackAtom).length,
});

export const useGridUndo = (maxStackSize = 20) => {
    const redoStackCount = useRecoilValue(redoStackCountSelector);
    const undoStackCount = useRecoilValue(undoStackCountSelector);
    const { purgeStacks } = useGridActionHistory(maxStackSize);
    const [loading, setLoading] = useState(false);

    const undo = useRecoilCallback(
        ({ snapshot, set }) =>
            async () => {
                const past = [...(await snapshot.getPromise(pastUndoStackAtom))];
                const future = [...(await snapshot.getPromise(futureRedoStackAtom))];
                const undoPoint = past.pop();
                if (undoPoint) {
                    const undoFn = undoPoint[0];
                    const err = await runVoidCallbackAsync(() => undoFn(snapshot, set));
                    set(pastUndoStackAtom, past);
                    set(futureRedoStackAtom, [...future, undoPoint]);
                    set(isInPastAtom, true);
                    if (err) {
                        throw err;
                    }
                }
            },
        [],
    );

    const redo = useRecoilCallback(
        ({ snapshot, set }) =>
            async () => {
                const past = [...(await snapshot.getPromise(pastUndoStackAtom))];
                const future = [...(await snapshot.getPromise(futureRedoStackAtom))];
                const redoPoint = future.pop();
                if (redoPoint) {
                    const redoFn = redoPoint[1];
                    const err = await runVoidCallbackAsync(() => redoFn(snapshot, set));
                    set(futureRedoStackAtom, future);
                    set(pastUndoStackAtom, [...resizeStackImmutable(past, maxStackSize - 1), redoPoint]);
                    if (err) {
                        throw err;
                    }
                }
            },
        [maxStackSize],
    );

    const shadowUndo = useCallback(() => {
        setLoading(true);
        return undo().finally(() => {
            setLoading(false);
        });
    }, [undo]);

    const shadowRedo = useCallback(() => {
        setLoading(true);
        return redo().finally(() => {
            setLoading(false);
        });
    }, [redo]);

    // We do this shadowing to prevent the undo/redo UI from flickering.
    // This happens because the undo state is part of the recoil state itself.
    // We keep providing the most recent value until the state has settled after
    // the undo/redo action and network calls are done.
    const lastRedoStackCount = useRef(redoStackCount);
    const shadowRedoStackCount = useMemo(() => {
        if (loading) {
            return lastRedoStackCount.current;
        }
        lastRedoStackCount.current = redoStackCount;
        return redoStackCount;
    }, [loading, redoStackCount]);

    const lastUndoStackCount = useRef(undoStackCount);
    const shadowUndoStackCount = useMemo(() => {
        if (loading) {
            return lastUndoStackCount.current;
        }
        lastUndoStackCount.current = undoStackCount;
        return undoStackCount;
    }, [loading, undoStackCount]);

    return {
        undo: shadowUndo,
        redo: shadowRedo,
        purge: purgeStacks,
        loading,
        redoStackCount: shadowRedoStackCount,
        undoStackCount: shadowUndoStackCount,
    };
};

export const useGridActionHistory = (maxStackSize = 20) => {
    const addAction = useRecoilCallback(
        ({ snapshot, set }) =>
            async (...undoPoint: UndoPoint) => {
                const past = await snapshot.getPromise(pastUndoStackAtom);
                const newPastStack = resizeStackImmutable(past, maxStackSize - 1);
                const isInPast = await snapshot.getPromise(isInPastAtom);
                set(pastUndoStackAtom, [...newPastStack, undoPoint]);
                if (isInPast) {
                    const future = [...(await snapshot.getPromise(futureRedoStackAtom))];
                    // The current future timeline is no longer valid, nuke it
                    set(futureRedoStackAtom, resizeStackImmutable(future));
                    set(isInPastAtom, false);
                }
                return newPastStack.length;
            },
        [maxStackSize],
    );

    const purgeStacks = useRecoilCallback(
        ({ set }) =>
            async () => {
                set(pastUndoStackAtom, resizeStackImmutable);
                set(futureRedoStackAtom, resizeStackImmutable);
            },
        [],
    );

    const unstable_quietlySpliceUndoStack = useRecoilCallback(
        ({ set }) =>
            async (start: number, deleteCount: number, ...actions: UndoPoint[]) => {
                set(pastUndoStackAtom, stack => {
                    return stack.toSpliced(start, deleteCount, ...actions);
                });
            },
        [],
    );

    const unstable_quietlySpliceRedoStack = useRecoilCallback(
        ({ set }) =>
            async (start: number, deleteCount: number, ...actions: UndoPoint[]) => {
                set(futureRedoStackAtom, stack => {
                    return stack.toSpliced(start, deleteCount, ...actions);
                });
            },
        [],
    );

    useLayoutEffect(() => {
        const purge = purgeStacks;
        return () => {
            purge();
        };
    }, [purgeStacks]);

    return {
        addAction,
        purgeStacks,
        /**
         * EASY TO MAKE MEMORY LEAKS IF CLEANUP ISN'T HANDLED PROPERLY. THIS DOES *_NOT_* RUN CLEANUPS, use with caution.
         * This is only for internal grid state use.
         * Acts like Array.prototype.splice
         */
        unstable_quietlySpliceUndoStack,
        /**
         * EASY TO MAKE MEMORY LEAKS IF CLEANUP ISN'T HANDLED PROPERLY. THIS DOES *_NOT_* RUN CLEANUPS, use with caution.
         * This is only for internal grid state use.
         * Acts like Array.prototype.splice
         */
        unstable_quietlySpliceRedoStack,
    };
};

const resizeStackImmutable = (stack: UndoPoint[], newSize = 0) => {
    const newStack = [...stack];
    if (newSize < 0 || newSize > newStack.length || newStack.length === 0) {
        return newStack;
    }
    while (newStack.length > newSize) {
        const cleanUp = newStack.pop()[2];
        if (cleanUp) {
            cleanUp();
        }
    }
    return newStack;
};
